import { useFlyerboardPagination } from './useFlyerboardPagination';
import { AllFlyerFilters, OwningTypeEnum } from '@/graphql/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { FlyerType } from '@/config/constants';
import { useURLSearchParams } from './useURLSearchParams';
import AuthenticationContext from '@/context/AuthenticationContext';

interface JarFlyerboardPaginationProps {
  audienceId?: string;
  districtId?: string | null;
  flyerboardFilters: AllFlyerFilters;
  skip?: boolean;
  skipExpandedCommunityFlyers?: boolean;
}

export function useJarFlyerboardPagination(props: JarFlyerboardPaginationProps) {
  const {
    audienceId,
    districtId,
    flyerboardFilters,
    skip = false,
    skipExpandedCommunityFlyers = false,
  } = props;
  const { community: userCommunity } = useContext(UserPreferencesContext);
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { lat, lng } = userCommunity?.location || {};
  const [shouldLoadExpandedFlyers, setShouldLoadExpandedFlyers] = useState(false);
  const flyerTypeMetrics = FlyerType.school;

  const { audienceType } = useURLSearchParams();

  const isSchoolFlyerboard = useMemo(
    () => !!(audienceType === 'school' && audienceId && districtId && audienceId !== districtId),
    [audienceType, audienceId, districtId]
  );

  const shouldHideExpandedCommunityFlyers = !isAuthenticated && (isSchoolFlyerboard || !districtId);

  const {
    flyers,
    loading,
    fetchMoreFlyers: fetchMoreRegularFlyers,
    total,
    dataNotFound,
    isPaginationEnd,
  } = useFlyerboardPagination({
    audienceId,
    districtId,
    flyerType: flyerTypeMetrics,
    flyerboardFilters: {
      ...flyerboardFilters,
    },
    skip,
  });

  const {
    flyers: expandedFlyers,
    loading: expandedLoading,
    fetchMoreFlyers: fetchMoreExpandedFlyers,
    total: expandedTotal,
    dataNotFound: expandedDataNotFound,
    isPaginationEnd: isExpandedPaginationEnd,
  } = useFlyerboardPagination({
    audienceId,
    districtId,
    flyerboardFilters: {
      ...flyerboardFilters,
      communityLocation: userCommunity?.location ? { lat, lng } : undefined,
      communityRadius: userCommunity?.distance ? userCommunity.distance : undefined,
    },
    flyerType: flyerTypeMetrics,
    skip: skipExpandedCommunityFlyers || !shouldLoadExpandedFlyers,
  });

  useEffect(() => {
    if (shouldHideExpandedCommunityFlyers) return;

    setShouldLoadExpandedFlyers(false);
    if (isPaginationEnd && !flyerboardFilters.owningType?.includes(OwningTypeEnum.district)) {
      setShouldLoadExpandedFlyers(true);
    }
  }, [flyerboardFilters, isPaginationEnd, shouldHideExpandedCommunityFlyers]);

  const combinedFlyers = useMemo(() => {
    const allFlyers = [...flyers, ...expandedFlyers];
    const uniqueFlyers = allFlyers.filter(
      (flyer, index, self) => index === self.findIndex(f => f.flyerId === flyer.flyerId)
    );
    return uniqueFlyers;
  }, [flyers, expandedFlyers]);

  const combinedTotal = total + expandedTotal;
  const combinedLoading = loading || (shouldLoadExpandedFlyers && expandedLoading);
  const combinedDataNotFound = dataNotFound && (!shouldLoadExpandedFlyers || expandedDataNotFound);
  const combinedIsPaginationEnd =
    isPaginationEnd || (shouldLoadExpandedFlyers && isExpandedPaginationEnd);

  const fetchMoreFlyers = () => {
    if (!isPaginationEnd) {
      fetchMoreRegularFlyers();
    } else if (shouldLoadExpandedFlyers && !isExpandedPaginationEnd) {
      fetchMoreExpandedFlyers();
    }
  };

  return {
    flyers: combinedFlyers,
    loading: combinedLoading,
    fetchMoreFlyers,
    total: combinedTotal,
    dataNotFound: combinedDataNotFound,
    isPaginationEnd: combinedIsPaginationEnd,
  };
}